import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Tag, Space, Button, Tooltip } from 'antd';
import { PushpinOutlined, PushpinFilled } from '@ant-design/icons';
import { useModelSelector } from '../../contexts/ModelSelectorContext';
import { useChat } from '../../contexts/ChatContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useUser } from '../../contexts/UserContext';
import { useChatUI } from '../../contexts/ChatUIContext';
import { ModelType } from '../../types/models';
import { sampleSidebarService } from '../../services/mock/sampleSidebarService';
import { ThreadHistory } from '../ThreadHistory';

const { Text } = Typography;

export const RecentThreads: React.FC = () => {
  const { dataSource } = useModelSelector();
  const { threads, selectThread: chatSelectThread, pinThread, unpinThread, loadUserThreads } = useChat();
  const { selectThread } = useNavigation();
  const { user, isAuthenticated } = useUser();
  const { model: chatUIModel } = useChatUI();
  const [sampleThreads, setSampleThreads] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (dataSource === 'sample') {
      void loadSampleThreads();
    }
  }, [dataSource]);

  const loadSampleThreads = async () => {
    try {
      const threads = await sampleSidebarService.getSampleThreads();
      setSampleThreads(threads);
    } catch (error) {
      console.error('Failed to load sample threads:', error);
    }
  };

  const displayThreads = dataSource === 'sample' ? sampleThreads : threads;

  const handleThreadClick = (thread: any) => {
    // Use navigation context to select the thread (it will also handle chat context)
    void selectThread(thread.id);
  };

  const handlePinToggle = async (thread: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent thread selection when clicking pin button
    
    try {
      console.log('📌 [RecentThreads] Toggling pin for thread:', {
        threadId: thread.id,
        threadName: thread.name,
        currentIsPinned: thread.isPinned
      });
      if (thread.isPinned) {
        await unpinThread(thread.id);
      } else {
        await pinThread(thread.id);
      }
      console.log('✅ [RecentThreads] Pin toggle successful');
      
      // Update refresh key to force re-render without reloading threads
      // (reloading causes duplicate threads)
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('❌ [RecentThreads] Failed to toggle pin status:', error);
    }
  };

  // Filter threads to show only recent, non-pinned threads that match ChatUI model context
  // Use refreshKey to force re-computation when threads are updated
  const recentThreads = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [RecentThreads] Filtering threads:', {
        totalThreads: displayThreads.length,
        chatUIModel: chatUIModel ? JSON.stringify(chatUIModel, null, 2) : 'NO MODEL',
        allThreads: displayThreads.map(t => ({
          id: t.id,
          name: t.name,
          isPinned: t.isPinned,
          model: t.model ? JSON.stringify(t.model, null, 2) : 'NO MODEL'
        }))
      });
    }
    
    const filtered = displayThreads
      .filter(thread => {
        // First check if not pinned
        const isPinned = thread.isPinned === true || thread.isPinned === 'true' || thread.is_pinned === true;
        if (isPinned) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`❌ [RecentThreads] Thread ${thread.name} (${thread.id}) is pinned, filtering out`);
          }
          return false;
        }
        
        // Then check if matches ChatUI model context
        if (!chatUIModel) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`⚠️ [RecentThreads] No ChatUI model context, filtering out thread ${thread.name} (${thread.id})`);
          }
          return false;
        }
        
        if (!thread.model) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`❌ [RecentThreads] Thread ${thread.name} (${thread.id}) has no model, filtering out`);
          }
          return false;
        }
        
        let matches = false;
        if (chatUIModel.type === ModelType.PRIME) {
          matches = thread.model.type === ModelType.PRIME && 
                   thread.model.artifactId === chatUIModel.artifactId;
        } else if (chatUIModel.type === ModelType.COMPANY) {
          matches = thread.model.type === ModelType.COMPANY && 
                   thread.model.artifactTreeId === chatUIModel.artifactTreeId;
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔍 [RecentThreads] Thread ${thread.name} (${thread.id}):`, {
            threadModel: JSON.stringify(thread.model, null, 2),
            chatUIModel: JSON.stringify(chatUIModel, null, 2),
            matches,
            reason: !matches ? (
              chatUIModel.type === ModelType.PRIME 
                ? `Type mismatch or artifactId mismatch: thread=${thread.model.type}/${thread.model.artifactId}, chatUI=${chatUIModel.type}/${chatUIModel.artifactId}`
                : `Type mismatch or artifactTreeId mismatch: thread=${thread.model.type}/${thread.model.artifactTreeId}, chatUI=${chatUIModel.type}/${chatUIModel.artifactTreeId}`
            ) : 'MATCHES'
          });
        }
        
        return matches;
      })
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()) // Sort by most recent
      .slice(0, 3); // Show the last 3 threads
    
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ [RecentThreads] Filtered threads result:', {
        totalThreads: displayThreads.length,
        filteredCount: filtered.length,
        filteredThreadIds: filtered.map(t => ({ id: t.id, name: t.name }))
      });
    }
    
    return filtered;
  }, [displayThreads, refreshKey, chatUIModel]);

  // Show message when no user is selected
  if (!isAuthenticated || !user) {
    return (
      <div style={{ 
        padding: '8px 12px', 
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '12px'
      }}>
        Select a user to view recent threads
      </div>
    );
  }

  return (
    <div>
      {/* Thread History button in top right */}
      <div style={{ 
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '8px'
      }}>
        <ThreadHistory />
      </div>
      
      {recentThreads.length === 0 ? (
        <div style={{ 
          padding: '8px 12px', 
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '12px'
        }}>
          No recent threads
        </div>
      ) : (
        recentThreads.map((thread) => (
        <div
          key={thread.id}
          onClick={() => handleThreadClick(thread)}
          style={{
            padding: '8px 12px',
            marginBottom: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '6px',
            cursor: 'pointer',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text 
                style={{ 
                  color: 'white', 
                  fontSize: '12px', 
                  fontWeight: 500,
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                title={thread.name}
              >
                {thread.name}
              </Text>
              <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                <Tag color="green">
                  {thread.messageCount} msgs
                </Tag>
                {thread.isPinned && (
                  <Tag color="orange">
                    <PushpinOutlined />
                  </Tag>
                )}
              </div>
            </div>
            <Tooltip title={thread.isPinned ? "Unpin thread" : "Pin thread"}>
              <Button
                type="text"
                size="small"
                icon={thread.isPinned ? <PushpinFilled /> : <PushpinOutlined />}
                onClick={(e) => handlePinToggle(thread, e)}
                style={{ 
                  color: thread.isPinned ? '#faad14' : 'rgba(255, 255, 255, 0.45)',
                  padding: '2px 4px',
                  minWidth: 'auto'
                }}
              />
            </Tooltip>
          </div>
        </div>
        ))
      )}
    </div>
  );
};
